import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import {
  subscribeToUnreadSupport,
  getUserBusinessName,
  type UnreadSupportInfo,
} from '../services/firebase';

interface SupportNotificationsValue {
  unreadTotal: number;
  unreadByUser: Record<string, number>;
}

const SupportNotificationsContext = createContext<SupportNotificationsValue>({
  unreadTotal: 0,
  unreadByUser: {},
});

export const useSupportNotifications = () => useContext(SupportNotificationsContext);

/**
 * Toca um bipe curto (Web Audio API, sem arquivo de áudio).
 * O navegador só libera áudio após alguma interação do usuário — como o admin
 * fez login/navegou, o contexto normalmente já está liberado.
 */
let audioCtx: AudioContext | null = null;
const playBeep = () => {
  try {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    audioCtx = audioCtx || new Ctx();
    const ctx = audioCtx;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45);
    osc.start();
    osc.stop(ctx.currentTime + 0.45);
  } catch {
    // silencioso: som é opcional
  }
};

interface ProviderProps {
  enabled: boolean;
  children: React.ReactNode;
}

export const SupportNotificationsProvider: React.FC<ProviderProps> = ({ enabled, children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [unreadTotal, setUnreadTotal] = useState(0);
  const [unreadByUser, setUnreadByUser] = useState<Record<string, number>>({});
  const [toast, setToast] = useState<{ open: boolean; title: string; body: string }>({
    open: false,
    title: '',
    body: '',
  });

  // ids já conhecidos (para detectar apenas mensagens NOVAS) e flag de 1ª carga.
  const seenIdsRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);
  // Caminho atual em ref, para o callback não capturar um valor velho.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  const notifyNewMessage = useCallback(
    async (latest: NonNullable<UnreadSupportInfo['latest']>, count: number) => {
      const name = await getUserBusinessName(latest.userId);
      const title = count > 1 ? `${count} novas mensagens de suporte` : `Nova mensagem de ${name}`;
      const body = count > 1 ? `Última de ${name}: ${latest.msg}` : latest.msg;

      // 1) Som
      playBeep();

      // 2) Toast dentro do painel
      setToast({ open: true, title, body });

      // 3) Notificação do navegador (mesmo com a aba em segundo plano)
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          const n = new Notification(title, {
            body,
            icon: '/images/logoMeuNegocio-SF.png',
            tag: 'support-new-message',
          });
          n.onclick = () => {
            window.focus();
            navigate('/support');
            n.close();
          };
        } catch {
          // alguns navegadores exigem Service Worker; o toast cobre esse caso
        }
      }
    },
    [navigate]
  );

  useEffect(() => {
    if (!enabled) {
      // Sessão encerrada: zera tudo para a próxima sessão começar limpa.
      setUnreadTotal(0);
      setUnreadByUser({});
      seenIdsRef.current = new Set();
      initializedRef.current = false;
      return;
    }

    // Pede permissão de notificação no primeiro gesto do usuário (o Safari exige
    // interação para exibir o prompt). O listener se remove após a primeira vez.
    let removeGestureListener = () => {};
    if ('Notification' in window && Notification.permission === 'default') {
      const requestOnce = () => {
        removeGestureListener();
        Notification.requestPermission().catch(() => {});
      };
      window.addEventListener('pointerdown', requestOnce, { once: true });
      removeGestureListener = () => window.removeEventListener('pointerdown', requestOnce);
    }

    const unsubscribe = subscribeToUnreadSupport((info) => {
      setUnreadTotal(info.total);
      setUnreadByUser(info.byUser);

      const currentSet = new Set(info.messageIds);

      // 1ª carga: apenas registra o estado, sem notificar mensagens antigas.
      if (!initializedRef.current) {
        initializedRef.current = true;
        seenIdsRef.current = currentSet;
        return;
      }

      const newIds = info.messageIds.filter((id) => !seenIdsRef.current.has(id));
      seenIdsRef.current = currentSet;

      if (newIds.length === 0) return;
      // Não perturba se o admin já está na própria tela de Suporte.
      if (pathnameRef.current === '/support') return;
      if (!info.latest) return;

      notifyNewMessage(info.latest, newIds.length);
    });

    return () => {
      removeGestureListener();
      unsubscribe();
    };
  }, [enabled, notifyNewMessage]);

  const handleToastClose = (_?: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setToast((t) => ({ ...t, open: false }));
  };

  return (
    <SupportNotificationsContext.Provider value={{ unreadTotal, unreadByUser }}>
      {children}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity="info"
          variant="filled"
          icon={false}
          onClose={handleToastClose}
          onClick={() => {
            setToast((t) => ({ ...t, open: false }));
            navigate('/support');
          }}
          sx={{ cursor: 'pointer', minWidth: 280, boxShadow: 4 }}
        >
          <strong>{toast.title}</strong>
          <div style={{ fontSize: 13, marginTop: 2, opacity: 0.95 }}>
            {toast.body.length > 80 ? toast.body.slice(0, 80) + '…' : toast.body}
          </div>
        </Alert>
      </Snackbar>
    </SupportNotificationsContext.Provider>
  );
};
